import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AuditVersion } from '@/hooks/useAmazonAudit'
import { AuditVersionSelector } from './AuditVersionSelector'

const SECTIONS = [
  { id: 'product-research', key: 'productResearch' },
  { id: 'product-summary', key: 'productSummary' },
  { id: 'title-analysis', key: 'titleAnalysis' },
  { id: 'brand-analysis', key: 'brandAnalysis' },
  { id: 'bullets-analysis', key: 'bulletsAnalysis' },
  { id: 'description-analysis', key: 'descriptionAnalysis' },
  { id: 'image-audit', key: 'imageAudit' },
  { id: 'keyword-matrix', key: 'keywordMatrix' },
  { id: 'pricing-analysis', key: 'pricingAnalysis' },
  { id: 'review-insights', key: 'reviewInsights' },
  { id: 'conversion-drivers', key: 'conversionDrivers' },
  { id: 'conversion-blockers', key: 'conversionBlockers' },
  { id: 'strengths-weaknesses', key: 'strengths' },
  { id: 'product-improvements', key: 'productImprovements' },
  { id: 'compliance', key: 'complianceCheck' },
  { id: 'attribute-intelligence', key: 'attributeIntelligence' },
  { id: 'competitive-benchmark', key: 'competitiveBenchmark' },
  { id: 'positioning', key: 'positioning' },
  { id: 'channel-readiness', key: 'channelReadiness' },
]

export { SECTIONS }

interface AuditSidebarProps {
  activeSection: string
  auditMode?: string
  versions?: AuditVersion[]
  currentVersion?: number
  productId?: string
}

export function AuditSidebar({ activeSection, auditMode, versions, currentVersion, productId }: AuditSidebarProps) {
  const { t } = useTranslation()

  const filteredSections = useMemo(() => {
    return SECTIONS.filter(s => {
      if (auditMode !== 'new_product' && s.id === 'product-research') return false
      if (auditMode === 'new_product' && s.id === 'compliance') return false
      if (auditMode !== 'channel_product' && s.id === 'channel-readiness') return false
      return true
    })
  }, [auditMode])

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    <nav className="sticky top-16 space-y-0.5 max-h-[calc(100vh-8rem)] overflow-y-auto">
      {versions && currentVersion !== undefined && (
        <AuditVersionSelector versions={versions} currentVersion={currentVersion} productId={productId} />
      )}
      {filteredSections.map(({ id, key }) => (
        <SidebarLink
          key={id}
          id={id}
          label={t(`amazonListing.report.${key}`)}
          active={activeSection === id}
          onClick={scrollTo}
        />
      ))}
    </nav>
  )
}

function SidebarLink({ id, label, active, onClick }: {
  id: string; label: string; active: boolean; onClick: (id: string) => void
}) {
  return (
    <button
      onClick={() => onClick(id)}
      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
        active
          ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold'
          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 font-medium'
      }`}
    >
      {label}
    </button>
  )
}
